
import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { Button } from '../components/shared/Button';
import { ContactButtons } from '../components/shared/ContactButtons';
import { DownloadDropdown } from '../components/shared/DownloadDropdown';
import { FreeTrialButton } from '../components/shared/FreeTrialButton';
import { ServiceReviews } from '../components/shared/ServiceReviews'; // Importazione
import { ProcessedReviews } from '../components/shared/ProcessedReviews'; // NEW
import { InfiniteScrollReviews } from '../components/shared/InfiniteScrollReviews'; // NEW
import {
  ArrowLeft,
  BarChart3,
  Bell,
  Layout,
  TrendingUp,
  Gift,
  Download,
  ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES, EXTERNAL_LINKS, SERVICE_REVIEWS, DOCUMENTS } from '../constants'; // Importazione Dati

export const AlgoEnginePage: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="bg-[#0B1120] text-white min-h-screen pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="mb-12">
          <Link to={ROUTES.FINANCE} className="flex items-center text-gray-400 hover:text-white transition-colors group">
            <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" />
            {t('common.back')}
          </Link>
        </div>

        <section className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <AnimatedSection>
              <div className="inline-block mb-6">
                <span className="bg-orange-500/10 text-orange-400 border border-orange-500/20 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest">
                  {t('finance.services.algoEngine.badge')}
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight mb-6">
                CTC <span className="text-orange-500">ALGO ENGINE 2.0</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-10">
                {t('finance.services.algoEngine.detailedDesc')}
              </p>

              <div className="flex flex-wrap gap-4 mb-6 relative">
                {/* PRICELIST DROPDOWN */}
                <DownloadDropdown
                  buttonText={t('finance.services.algoEngine.pricelistBtn')}
                  forceDirection="down"
                  options={[
                    { label: '🇮🇹 Italiano', url: DOCUMENTS.ALGO_ENGINE_PRICELIST_IT },
                    { label: '🇬🇧 English', url: DOCUMENTS.ALGO_ENGINE_PRICELIST_EN }
                  ]}
                />
              </div>

              {/* Free Trial Button */}
              <div className="relative z-[100] overflow-visible mb-24">
                <FreeTrialButton
                  serviceName="CTC Algo Engine 2.0"
                  forceDirection="down"
                  forceNoMargin
                />
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200} className="pt-16">
              {/* Updating Banner */}
              <div className="mb-10 rounded-2xl border border-orange-500/30 bg-gradient-to-br from-orange-500/10 via-orange-500/5 to-transparent p-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(249,115,22,0.12),transparent_70%)]" />
                <div className="relative flex items-start gap-4">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-orange-500/20 border border-orange-500/30 flex items-center justify-center mt-0.5">
                    <svg className="w-5 h-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-orange-400 font-bold text-sm uppercase tracking-widest mb-1">{(t('finance.services.algoEngine.updating') as any).title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{(t('finance.services.algoEngine.updating') as any).subtitle}</p>
                    <p className="text-orange-300 text-sm font-semibold mt-1">{(t('finance.services.algoEngine.updating') as any).cta}</p>
                  </div>
                </div>
              </div>

              <div className="bg-surface-dark border border-gray-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden group z-0">
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Layout size={120} />
                </div>
                <h3 className="text-2xl font-bold mb-6 text-white uppercase tracking-wider">{t('finance.services.algoEngine.dashboard.title')}</h3>
                <p className="text-gray-400 text-sm mb-8 leading-relaxed">
                  {t('finance.services.algoEngine.dashboard.desc')}
                </p>

                {/* Link to Dashboard */}
                <Link
                  to={ROUTES.ALGO_ENGINE_DASHBOARD}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-orange-500/30"
                >
                  <Layout size={20} />
                  Apri Dashboard Live
                  <ExternalLink size={16} />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Recent Performance Section */}
        <section className="mb-20">
          <AnimatedSection>
            <h2 className="text-2xl font-bold mb-8 flex items-center">
              <TrendingUp className="mr-3 text-orange-500" />
              {t('common.recentPerformance')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {(t('finance.services.algoEngine.specs') as any[]).map((spec, i) => (
                <div key={i} className="bg-[#151E32] border border-gray-800 rounded-xl p-6 text-center hover:border-orange-500/30 transition-colors">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">{spec.label}</div>
                  <div className="text-2xl font-black text-white">{spec.value}</div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </section>

        {/* RISULTATI E FEEDBACK */}
        <InfiniteScrollReviews
          service="Algo Engine"
          autoScroll={true}
        />

      </div>
    </div>
  );
};
